/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { IPatient } from '@ahryman40k/ts-fhir-types/lib/R4';

const LEGACY_TEST_PATIENT_CREATED_AT = '2025-02-27';

const fhirDateKeys = new Set([
  'abatementDateTime',
  'authoredOn',
  'birthDate',
  'created',
  'date',
  'effectiveDateTime',
  'effectiveInstant',
  'end',
  'issued',
  'occurrenceDateTime',
  'onsetDateTime',
  'performedDateTime',
  'recordedDate',
  'start',
]);

function addCalendarOffset(date: Date, months: number) {
  const targetMonth = date.getUTCMonth() + months;
  const targetYear = date.getUTCFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, normalizedMonth + 1, 0)).getUTCDate();
  return new Date(Date.UTC(targetYear, normalizedMonth, Math.min(date.getUTCDate(), lastDayOfTargetMonth)));
}

function shiftIsoDate(value: string, months: number) {
  const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(.*)$/);
  if (!match) return value;
  const shiftedDate = addCalendarOffset(
    new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))),
    months
  );
  return shiftedDate.toISOString().slice(0, 10) + match[4];
}

function ageOnDate(birthDate: string, date: Date) {
  const match = birthDate.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!match) return undefined;
  const birthYear = Number(match[1]);
  const birthMonth = Number(match[2]) - 1;
  const birthDay = Number(match[3]);
  let age = date.getUTCFullYear() - birthYear;
  if (date.getUTCMonth() < birthMonth || (date.getUTCMonth() === birthMonth && date.getUTCDate() < birthDay)) {
    age--;
  }
  return age;
}

function reconcileTestPatientAge(patientData: unknown, now: Date) {
  const patient = (patientData as { patient?: IPatient }).patient;
  const namedAge = patient?.name
    ?.map((name) => name.text?.match(/\b(\d+)\s+year old\b/i)?.[1])
    .find((age) => age !== undefined);
  if (!patient?.birthDate || namedAge === undefined) return;

  const currentAge = ageOnDate(patient.birthDate, now);
  if (currentAge !== undefined) {
    patient.birthDate = shiftIsoDate(patient.birthDate, (currentAge - Number(namedAge)) * 12);
  }
}

export function adjustTestPatientDates<T extends object>(patientData: T, now = new Date()): T {
  const createdAtValue = (patientData as { createdAt?: string }).createdAt;
  const createdAt = new Date(createdAtValue || LEGACY_TEST_PATIENT_CREATED_AT);
  const createdDate = new Date(Date.UTC(createdAt.getUTCFullYear(), createdAt.getUTCMonth(), createdAt.getUTCDate()));
  const currentDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const elapsedYears = (currentDate.getTime() - createdDate.getTime()) / (365.2425 * 24 * 60 * 60 * 1000);
  const months = Math.round(elapsedYears) * 12;

  function shiftDates(value: unknown, key?: string): unknown {
    if (typeof value === 'string') return key && fhirDateKeys.has(key) ? shiftIsoDate(value, months) : value;
    if (Array.isArray(value)) return value.map((item) => shiftDates(item));
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([childKey, child]) => [childKey, shiftDates(child, childKey)])
      );
    }
    return value;
  }

  const shiftedPatientData = shiftDates(patientData) as T;
  reconcileTestPatientAge(shiftedPatientData, now);
  return shiftedPatientData;
}
